import React from "react";
import PropTypes from "prop-types";
import { Button, Input } from "reactstrap";

const selectStyle = { width: "auto", minWidth: 110 };

/**
 * Filter controls for the camera store-tree: free-text search, active/inactive
 * status, inference machine, alert-routing segment, and a has-polygon toggle.
 * Purely presentational — it owns no state; the parent holds the `filters` object
 * and applies them via filterTree.
 */
export default function TreeFilterBar({
  filters,
  machineOptions,
  segmentOptions,
  matchCount,
  filterActive,
  onChange,
  onClear,
}) {
  const set = (patch) => onChange({ ...filters, ...patch });

  return (
    <div className="px-2 mb-2">
      <div className="d-flex flex-wrap align-items-center" style={{ gap: 6 }}>
        <Input
          type="search"
          bsSize="sm"
          placeholder="Search id, name, slot…"
          value={filters.query}
          onChange={(e) => set({ query: e.target.value })}
          style={{ flex: "1 1 160px", minWidth: 140 }}
        />
        <Input
          type="select"
          bsSize="sm"
          value={filters.status}
          onChange={(e) => set({ status: e.target.value })}
          className="custom-select custom-select-sm"
          style={selectStyle}
          aria-label="Filter by status"
        >
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Input>
        <Input
          type="select"
          bsSize="sm"
          value={filters.machineId == null ? "" : String(filters.machineId)}
          onChange={(e) => set({ machineId: e.target.value === "" ? null : Number(e.target.value) })}
          className="custom-select custom-select-sm"
          style={selectStyle}
          aria-label="Filter by inference machine"
        >
          <option value="">Any machine</option>
          {machineOptions.map((m) => (
            <option key={m.id} value={String(m.id)}>
              {m.label}
            </option>
          ))}
        </Input>
        {segmentOptions.length > 0 && (
          <Input
            type="select"
            bsSize="sm"
            value={filters.segment}
            onChange={(e) => set({ segment: e.target.value })}
            className="custom-select custom-select-sm"
            style={selectStyle}
            aria-label="Filter by alert-routing segment"
          >
            <option value="">Any segment</option>
            {segmentOptions.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </Input>
        )}
        <label
          className="d-flex align-items-center text-muted mb-0"
          style={{ gap: 4, fontSize: "0.8rem", whiteSpace: "nowrap" }}
          title="Only views that have a polygon drawn"
        >
          <input
            type="checkbox"
            checked={!!filters.hasPolygon}
            onChange={(e) => set({ hasPolygon: e.target.checked })}
          />
          <span>
            <i className="fas fa-draw-polygon mr-1" />
            Has polygon
          </span>
        </label>
        {filterActive && (
          <Button size="sm" outline color="light" onClick={onClear} title="Clear filters">
            ✕
          </Button>
        )}
      </div>
      {filterActive && (
        <div className="text-muted mt-1" style={{ fontSize: "0.72rem" }}>
          {matchCount} match{matchCount === 1 ? "" : "es"}
        </div>
      )}
    </div>
  );
}

TreeFilterBar.propTypes = {
  filters: PropTypes.shape({
    query: PropTypes.string,
    status: PropTypes.string,
    machineId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    segment: PropTypes.string,
    hasPolygon: PropTypes.bool,
  }).isRequired,
  machineOptions: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, label: PropTypes.string.isRequired })
  ).isRequired,
  segmentOptions: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired })
  ).isRequired,
  matchCount: PropTypes.number.isRequired,
  filterActive: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};
